import { Link, usePathname } from "expo-router";
import { useMemo, type JSX } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { fontFamily, space } from "@passfirst/ui";

import { NAV_ITEMS, pathToTab } from "./nav-items";
import { type Colors, useColors } from "./theme";

// Bottom tab bar for the paid-product surface (native/narrow). On
// web/wide this is replaced by the left sidebar — see TabScaffold /
// AppSidebar (design_handoff_2, Rule B). Nav destinations come from the
// shared NAV_ITEMS so the two surfaces never drift. Each tab is a Link
// to its route; expo-router treats it like normal navigation.

export function BottomTabBar(): JSX.Element {
  const colors = useColors();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const pathname = usePathname();
  const active = pathToTab(pathname);

  return (
    <View
      style={[styles.bar, { paddingBottom: Math.max(space[2], insets.bottom) }]}
      accessibilityRole="tablist"
    >
      {NAV_ITEMS.map((tab) => {
        const isActive = active === tab.id;
        const tabColor = isActive ? colors.accentSoftText : colors.textMuted;
        return (
          <Link key={tab.id} href={tab.href} asChild>
            <Pressable
              accessibilityRole="tab"
              accessibilityState={{ selected: isActive }}
              accessibilityLabel={`${tab.label} tab`}
              style={styles.tab}
            >
              <tab.icon color={tabColor} />
              <Text style={[styles.label, { color: tabColor }]}>
                {tab.label}
              </Text>
            </Pressable>
          </Link>
        );
      })}
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    bar: {
      flexDirection: "row",
      justifyContent: "space-around",
      paddingTop: space[2],
      paddingHorizontal: space[2],
      borderTopWidth: 1,
      borderTopColor: colors.borderSoft,
      backgroundColor: colors.surface,
    },
    tab: {
      flex: 1,
      alignItems: "center",
      gap: 3,
      paddingVertical: 2,
      paddingHorizontal: space[3],
    },
    label: {
      fontFamily: fontFamily.semiBold,
      fontSize: 10.5,
    },
  });
}
